import React, { useEffect, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../AuthContext";
import Modal from "react-modal"; // Import react-modal
import { saveAs } from "file-saver"; // For CSV downloads
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import "./Spinner.css"; // Import the CSS for spinner

// Register necessary components for Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// Set up modal styles
Modal.setAppElement("#root");
const modalStyles = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        width: "90%",
        maxWidth: "600px",
        maxHeight: "80%",
        overflowY: "auto", // Enable scrolling
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
    },
};

const doughnutColors = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#C9CBCF"];

const getSectorBreakdown = (binCompanies, binKeys) => {
    const allCompanies = binKeys.flatMap((key) => binCompanies[key] || []);
    if (allCompanies.length === 0) return { labels: [], data: [], total: 0 };

    const sectorCounts = {};
    allCompanies.forEach((c) => {
        const sector = c.basicIndustry || "Unknown";
        sectorCounts[sector] = (sectorCounts[sector] || 0) + 1;
    });

    const sorted = Object.entries(sectorCounts).sort((a, b) => b[1] - a[1]);
    const top5 = sorted.slice(0, 5);
    const othersCount = sorted.slice(5).reduce((sum, [, count]) => sum + count, 0);

    const labels = top5.map(([name]) => name);
    const data = top5.map(([, count]) => count);
    if (othersCount > 0) {
        labels.push("Others");
        data.push(othersCount);
    }

    return { labels, data, total: allCompanies.length };
};

const SectorDoughnut = ({ title, binCompanies, binKeys }) => {
    const { labels, data, total } = getSectorBreakdown(binCompanies, binKeys);

    if (total === 0) {
        return (
            <div style={{ flex: 1, textAlign: "center", padding: "10px" }}>
                <div style={{ fontSize: "11px", fontWeight: "bold", marginBottom: "4px" }}>{title}</div>
                <div style={{ color: "#999", fontSize: "12px" }}>No data</div>
            </div>
        );
    }

    const chartData = {
        labels,
        datasets: [{
            data,
            backgroundColor: doughnutColors.slice(0, data.length),
            borderWidth: 1,
        }],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true,
                text: `${title} (${total})`,
                font: { size: 11 },
            },
            legend: {
                position: "right",
                labels: { font: { size: 9 }, boxWidth: 10 },
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const pct = ((context.raw / total) * 100).toFixed(1);
                        return `${context.label}: ${context.raw} (${pct}%)`;
                    },
                },
            },
        },
    };

    return (
        <div style={{ flex: 1, height: "150px", padding: "0 5px" }}>
            <Doughnut data={chartData} options={options} />
        </div>
    );
};

const PriceDeviationHistogramInternal = () => {
    const { logout } = useAuth();
    const [rawData, setRawData] = useState([]); // Raw unfiltered API data
    const [latestSnapshotName, setLatestSnapshotName] = useState("");
    const [historicData, setHistoricData] = useState([]);
    const [showHistoricData, setShowHistoricData] = useState(false);
    const [loadingHistoric, setLoadingHistoric] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const [triggeringLambda, setTriggeringLambda] = useState(false);
    const [selectedDate, setSelectedDate] = useState("");
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedBinData, setSelectedBinData] = useState([]);
    const [expandedSegments, setExpandedSegments] = useState({});
    const [autoLoginStatus, setAutoLoginStatus] = useState(null);
    const [health, setHealth] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshMsg, setRefreshMsg] = useState(null);
    const refreshTimer = useRef(null);
    const navigate = useNavigate();

    const mcapSegments = [
        { label: "MCAP 100 - 500", lower: 100, upper: 500 },
        { label: "MCAP 500 - 2,500", lower: 500, upper: 2500 },
        { label: "MCAP 2,500 - 10,000", lower: 2500, upper: 10000 },
        { label: "MCAP 10,000 - 75,000", lower: 10000, upper: 75000 },
        { label: "MCAP 75,000+", lower: 75000, upper: Infinity },
    ];

    const handleBarClick = (event, elements, binCompaniesData) => {
        if (elements.length > 0) {
            const clickedBinIndex = elements[0].index; // Get the index of the clicked bin
            const binLabel = labels[clickedBinIndex]; // Get the bin label
            const companiesInBin = binCompaniesData[binLabel] || []; // Get companies in the bin
            // Sort companies by absolute value of price deviation (highest to lowest)
            const sortedCompanies = companiesInBin.sort((a, b) =>
                Math.abs(b.priceDeviation) - Math.abs(a.priceDeviation)
            );
            setSelectedBinData(sortedCompanies); // Set the data for the modal
            setModalIsOpen(true); // Open the modal
        }
    };

    const downloadCSV = () => {
        const csvContent = selectedBinData
            .map(
                (company) => `${company.name},${company.mcap ?? ""},${company.basicIndustry ?? ""}`
            )
            .join("\n");
        const blob = new Blob([`Company Name,MCAP,basicIndustry\n${csvContent}`], {
            type: "text/csv;charset=utf-8;",
        });
        saveAs(blob, "companies_in_bin.csv");
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const getBackendUrl = () => {
        const backendHost = window.location.hostname;
        const isSecure = window.location.protocol === 'https:';
        return isSecure
            ? `https://${backendHost}`
            : `http://${backendHost}:5001`;
    };

    const handleTriggerBreadth = async () => {
        setTriggeringLambda(true);
        try {
            const response = await axios.post(`${getBackendUrl()}/api/trigger_price_deviations`);
            alert(response.data.statusCode === 200
                ? "Breadth data refreshed successfully!"
                : `Lambda returned: ${JSON.stringify(response.data.body)}`);
            // Reload the page to fetch fresh data
            window.location.reload();
        } catch (error) {
            console.error("Error triggering breadth refresh:", error);
            alert("Failed to trigger breadth refresh. Check console for details.");
        } finally {
            setTriggeringLambda(false);
        }
    };

    const handleCheckTokenValidity = async () => {
        setLoading(true);
        const backendHost = window.location.hostname; // Derives the host of the frontend app
        const isSecure = window.location.protocol === 'https:';
        const backendUrl = isSecure
            ? `https://${backendHost}` // Use HTTPS and omit port if secure
            : `http://${backendHost}:5001`; // Use HTTP with port 5001 if not secure
        const backendEndpoint = `${backendUrl}/api/check_token_validity`;

        try {
            const response = await axios.get(backendEndpoint);
            const isTokenValid = response.data.is_access_token_in_s3_valid;
            alert(isTokenValid ? "The access token is valid." : "The access token is not valid.");
        } catch (error) {
            console.error("Error checking token validity:", error);
            alert("An error occurred while checking token validity.");
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshAccessToken = async () => {
        const requestToken = prompt("Please enter your request token:");
        if (!requestToken) {
            alert("Request token is required to refresh access token.");
            return;
        }
        setLoading(true);
        const backendHost = window.location.hostname; // Derives the host of the frontend app
        const isSecure = window.location.protocol === 'https:';
        const backendUrl = isSecure
            ? `https://${backendHost}` // Use HTTPS and omit port if secure
            : `http://${backendHost}:5001`; // Use HTTP with port 5001 if not secure
        const backendEndpoint = `${backendUrl}/api/refresh_access_token`;

        try {
            const response = await axios.post(backendEndpoint, { request_token: requestToken });
            alert(response.data.access_token ? "Access token refreshed successfully." : "Failed to refresh access token.");
        } catch (error) {
            console.error("Error refreshing access token:", error);
            alert("An error occurred while refreshing access token.");
        } finally {
            setLoading(false);
        }
    };

    const getIntervalForDate = (date) => {
        const today = new Date().toISOString().split("T")[0];
        return date === today ? 15 : 60;
    };

    const loadHistoricData = async () => {
        if (!selectedDate) {
            alert("Please select a date.");
            return;
        }

        setLoadingHistoric(true);
        const backendEndpoint = `${getBackendUrl()}/api/historic_price_deviations`;

        try {
            const response = await axios.post(backendEndpoint, {
                date: selectedDate,
                interval_minutes: getIntervalForDate(selectedDate),
            });
            setHistoricData(response.data);
            setShowHistoricData(true);
        } catch (error) {
            console.error("Error fetching historic data:", error);
        } finally {
            setLoadingHistoric(false);
        }
    };

    useEffect(() => {
        const fetchHistoricData = async () => {
            if (!selectedDate) return;

            setLoadingHistoric(true);
            const backendEndpoint = `${getBackendUrl()}/api/historic_price_deviations`;

            try {
                const response = await axios.post(backendEndpoint, {
                    date: selectedDate,
                    interval_minutes: getIntervalForDate(selectedDate),
                });
                setHistoricData(response.data);
                setShowHistoricData(true);
            } catch (error) {
                console.error("Error fetching historic data:", error);
            } finally {
                setLoadingHistoric(false);
            }
        };

        fetchHistoricData();
    }, [selectedDate]);

    const formatSnapshotName = (filename) => {
        // "last_price_deviations_2026-02-17_09-16-07.csv" → "17 Feb 2026, 09:16:07 IST"
        const match = filename.match(/(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})/);
        if (!match) return filename;
        const [, y, m, d, hh, mm, ss] = match;
        const date = new Date(`${y}-${m}-${d}T${hh}:${mm}:${ss}`);
        return date.toLocaleString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
        }) + " IST";
    };

    useEffect(() => {
        const backendEndpoint = `${getBackendUrl()}/api/last_price_deviations`;
        const fetchData = async () => {
            try {
                const response = await axios.get(backendEndpoint);
                const response_data = typeof response.data === "string"
                    ? JSON.parse(response.data)
                    : response.data;
                console.log("Latest:", response_data);
                setLatestSnapshotName(response_data.latest_snapshot_name);
                setRawData(response_data.data || []);
            } catch (error) {
                console.error("Error fetching price deviations:", error);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 15 * 60 * 1000); // Auto-refresh every 15 min
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        axios.get(`${getBackendUrl()}/api/auto_login_status`)
            .then((res) => setAutoLoginStatus(res.data))
            .catch(() => setAutoLoginStatus(null));
    }, []);

    // Poll pipeline health (token validity + data freshness) for the status banner
    useEffect(() => {
        const fetchHealth = () => {
            axios.get(`${getBackendUrl()}/api/pipeline_health`)
                .then((res) => setHealth(res.data))
                .catch(() => setHealth(null));
        };
        fetchHealth();
        const interval = setInterval(fetchHealth, 60 * 1000); // every 60s
        return () => clearInterval(interval);
    }, []);

    // The login is invoked async, so the only signal it worked is the token going valid.
    useEffect(() => {
        if (health && health.overall !== "login_failed") {
            if (refreshTimer.current) clearTimeout(refreshTimer.current);
            setRefreshing(false);
            setRefreshMsg(null);
        }
    }, [health]);

    useEffect(() => () => {
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
    }, []);

    const triggerAutoLogin = () => {
        setRefreshing(true);
        setRefreshMsg("Login triggered — takes ~30-60s. This banner clears itself once the token is valid.");
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        // A failed login leaves overall === "login_failed", so the effect above never
        // re-enables the button. Without this the user could never retry.
        refreshTimer.current = setTimeout(() => {
            setRefreshing(false);
            setRefreshMsg("Still not logged in. Check the last attempt message below, then try again.");
        }, 120 * 1000);
        axios.post(`${getBackendUrl()}/api/trigger_auto_login`)
            .catch((err) => {
                clearTimeout(refreshTimer.current);
                setRefreshing(false);
                setRefreshMsg(`Could not start auto-login: ${err.response?.data?.error || err.message}`);
            });
    };

    const toggleSegment = (label) => {
        setExpandedSegments((prev) => ({ ...prev, [label]: !prev[label] }));
    };

    // Filter historic snapshot data by MCAP range and compute bins
    const getHistoricSegmentBins = (historicItem, segment) => {
        const filteredData = historicItem.data.filter(
            (item) => item.mcap >= segment.lower && item.mcap < segment.upper
        );
        const deviations = filteredData.map((item) => item.price_deviation);
        const companies = filteredData.map((item) => ({
            name: item.instrument_name,
            mcap: item.mcap,
            basicIndustry: item.basicIndustry,
            priceDeviation: item.price_deviation,
        }));
        return getDeviationBins(deviations, companies);
    };

    // Compute bins for a given MCAP segment from raw data
    const getSegmentData = (segment) => {
        const filteredData = rawData.filter(
            (item) => item.mcap >= segment.lower && item.mcap < segment.upper
        );
        const deviations = filteredData.map((item) => item.price_deviation);
        const companiesWithMcap = filteredData.map((item) => ({
            name: item.instrument_name,
            mcap: item.mcap,
            basicIndustry: item.basicIndustry,
            priceDeviation: item.price_deviation,
        }));
        return getDeviationBins(deviations, companiesWithMcap);
    };

    const getDeviationBins = (deviations, companiesWithMcap) => {
        const bins = {
            "<-10%": { count: 0, companies: [] },
            "-10% to -5%": { count: 0, companies: [] },
            "-5% to -2%": { count: 0, companies: [] },
            "-2% to 0%": { count: 0, companies: [] },
            "0% to 2%": { count: 0, companies: [] },
            "2% to 5%": { count: 0, companies: [] },
            "5% to 10%": { count: 0, companies: [] },
            "> 10%": { count: 0, companies: [] },
        };

        deviations.forEach((deviation, index) => {
            const company = companiesWithMcap[index];
            if (deviation < -0.1) {
                bins["<-10%"].count++;
                bins["<-10%"].companies.push(company);
            } else if (deviation < -0.05) {
                bins["-10% to -5%"].count++;
                bins["-10% to -5%"].companies.push(company);
            } else if (deviation < -0.02) {
                bins["-5% to -2%"].count++;
                bins["-5% to -2%"].companies.push(company);
            } else if (deviation < 0) {
                bins["-2% to 0%"].count++;
                bins["-2% to 0%"].companies.push(company);
            } else if (deviation < 0.02) {
                bins["0% to 2%"].count++;
                bins["0% to 2%"].companies.push(company);
            } else if (deviation < 0.05) {
                bins["2% to 5%"].count++;
                bins["2% to 5%"].companies.push(company);
            } else if (deviation < 0.1) {
                bins["5% to 10%"].count++;
                bins["5% to 10%"].companies.push(company);
            } else {
                bins["> 10%"].count++;
                bins["> 10%"].companies.push(company);
            }
        });

        const totalItems = deviations.length;
        const binData = Object.keys(bins).map((key) => ({
            count: bins[key].count,
            percentage: ((bins[key].count / totalItems) * 100).toFixed(2),
        }));
        const binCompanies = Object.keys(bins).reduce((acc, key) => {
            acc[key] = bins[key].companies;
            return acc;
        }, {});

        return { binData, binCompanies };
    };

    const labels = [
        "<-10%",
        "-10% to -5%",
        "-5% to -2%",
        "-2% to 0%",
        "0% to 2%",
        "2% to 5%",
        "5% to 10%",
        "> 10%",
    ];

    const chartColors = ["#8B0000", "#B22222", "#DC143C", "#FF6347", "#90EE90", "#66CDAA", "#32CD32", "#006400"];

    // Function to generate dynamic options
    const getOptions = (binCompaniesData, title) => ({
        onClick: (event, elements) => handleBarClick(event, elements, binCompaniesData), // Dynamic binCompanies
        scales: {
            x: {
                title: {
                    display: true,
                    text: "Percentage Change Bins",
                    font: { size: 16 },
                },
            },
            y: {
                title: {
                    display: true,
                    text: "Number of Companies",
                    font: { size: 16 },
                },
                beginAtZero: true,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: !!title,
                text: title || "",
                font: { size: 14 },
            },
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const binLabel = context.label;
                        const count = context.raw;
                        const companiesInBin = binCompaniesData[binLabel] || [];
                        // Calculate percentage of companies in this bin
                        const totalCompanies = Object.values(binCompaniesData).reduce(
                            (sum, bin) => sum + bin.length,
                            0
                        );
                        const percentage = ((companiesInBin.length / totalCompanies) * 100).toFixed(2);

                        // Format company names with MCAP and industry
                        const companyDetails = companiesInBin.map(
                            (company) => `${company.name} (MCAP: ${company.mcap?.toFixed(2) || "N/A"}, Industry: ${company.basicIndustry || "N/A"})`
                        );

                        // Join details for the tooltip
                        const companyList = companyDetails.slice(0, 5).join(", ");
                        const moreText = companiesInBin.length > 5 ? ", ..." : "";
                        return [
                            `Count: ${count} instruments (${percentage}%)`,
                            `Companies: ${companyList}${moreText}`,
                        ];
                    },
                },
            },
        },
    });

    return (
        <div>
            {/* Nav Buttons */}
            <div style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                zIndex: 1000,
                display: "flex",
                gap: "8px"
            }}>
                <Link to="/marketdata">
                    <button style={{
                        background: "#17a2b8",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        padding: "8px 16px",
                        fontSize: "16px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
                    }}>
                        Market Data
                    </button>
                </Link>
                <Link to="/notifications">
                    <button style={{
                        background: "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        padding: "8px 16px",
                        fontSize: "16px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
                    }}>
                        Notifications
                    </button>
                </Link>
            </div>
            
            <div className={`sidebar ${isSidebarOpen ? "open" : "collapsed"}`}>
                <button onClick={toggleSidebar} className="toggle-button">
                    {isSidebarOpen ? "<" : ">"}
                </button>
                {isSidebarOpen && (
                    <div>
                        <h2>Options</h2>
                        <ul>
                            <li onClick={handleTriggerBreadth} style={{ cursor: triggeringLambda ? "wait" : "pointer" }}>
                                {triggeringLambda ? "Refreshing Breadth..." : "Refresh Breadth"}
                            </li>
                            <li onClick={handleCheckTokenValidity}>Check Token Validity</li>
                            <li onClick={handleRefreshAccessToken}>Refresh Access Token</li>
                        </ul>
                        {autoLoginStatus && (
                            <div style={{
                                marginTop: "15px",
                                padding: "8px 10px",
                                borderRadius: "6px",
                                fontSize: "12px",
                                backgroundColor: autoLoginStatus.status === "success" ? "#e6f9e6" : "#fde8e8",
                                border: `1px solid ${autoLoginStatus.status === "success" ? "#4caf50" : "#e53e3e"}`,
                            }}>
                                <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                                    Auto-Login: {autoLoginStatus.status === "success" ? "OK" : "FAILED"}
                                </div>
                                <div style={{ color: "#555" }}>
                                    {autoLoginStatus.user_name && `${autoLoginStatus.user_name} | `}
                                    {autoLoginStatus.timestamp && new Date(autoLoginStatus.timestamp).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div style={{width: "95%", margin: "auto"}}>
                <h2 style={{textAlign: "center", marginBottom: "10px"}}>Price Deviation Histogram</h2>
                {health && health.overall !== "ok" && (
                    <div style={{
                        maxWidth: "900px",
                        margin: "10px auto",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        textAlign: "center",
                        fontWeight: "bold",
                        backgroundColor: health.overall === "login_failed" ? "#fde8e8" : "#fff4e5",
                        border: `1px solid ${health.overall === "login_failed" ? "#e53e3e" : "#f59e0b"}`,
                        color: health.overall === "login_failed" ? "#b91c1c" : "#92400e",
                    }}>
                        {health.overall === "login_failed"
                            ? "⚠ Zerodha login failed — the API token is invalid, so the histograms are NOT updating."
                            : `⚠ Data fetch stale — last update ${health.latest_data_age_minutes != null ? health.latest_data_age_minutes + " min ago" : "unknown"}${health.latest_data_ist ? " (" + new Date(health.latest_data_ist).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true }) + " IST)" : ""}. The price-deviation pipeline may have stopped.`}
                        {health.overall === "login_failed" && (
                            <div style={{marginTop: "10px"}}>
                                <button
                                    onClick={triggerAutoLogin}
                                    disabled={refreshing}
                                    style={{
                                        padding: "6px 14px",
                                        borderRadius: "6px",
                                        border: "1px solid #b91c1c",
                                        backgroundColor: refreshing ? "#f3d0d0" : "#b91c1c",
                                        color: refreshing ? "#7a3030" : "#fff",
                                        cursor: refreshing ? "default" : "pointer",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {refreshing ? "Refreshing…" : "Re-run auto-login"}
                                </button>
                                {refreshMsg && (
                                    <div style={{marginTop: "8px", fontWeight: "normal", fontSize: "13px"}}>
                                        {refreshMsg}
                                    </div>
                                )}
                                {health.auto_login && health.auto_login.status === "failed" && (
                                    <div style={{marginTop: "8px", fontWeight: "normal", fontSize: "13px"}}>
                                        Last attempt failed: {health.auto_login.message}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {latestSnapshotName && (
                    <p style={{textAlign: "center", color: "#555", margin: "5px 0"}}>Last updated: {formatSnapshotName(latestSnapshotName)}</p>
                )}
                {rawData.length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px" }}>
                        {mcapSegments.map((segment, idx) => {
                            const { binData, binCompanies: segBinCompanies } = getSegmentData(segment);
                            const totalInSegment = binData.reduce((sum, b) => sum + b.count, 0);
                            const chartData = {
                                labels,
                                datasets: [{
                                    label: segment.label,
                                    data: binData.map((bin) => bin.count),
                                    backgroundColor: chartColors,
                                    borderColor: "#333",
                                    borderWidth: 1,
                                }],
                            };
                            const options = getOptions(segBinCompanies, `#${idx + 1} ${segment.label} (${totalInSegment} stocks)`);
                            return (
                                <div key={idx} style={{
                                    padding: "10px",
                                    backgroundColor: "#fff",
                                    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
                                    borderRadius: "8px",
                                }}>
                                    <div style={{ height: "300px" }}>
                                        <Bar data={chartData} options={options} />
                                    </div>
                                    <div style={{ display: "flex", marginTop: "5px" }}>
                                        <SectorDoughnut title="Bottom Sectors" binCompanies={segBinCompanies} binKeys={["<-10%", "-10% to -5%", "-5% to -2%"]} />
                                        <SectorDoughnut title="Top Sectors" binCompanies={segBinCompanies} binKeys={["2% to 5%", "5% to 10%", "> 10%"]} />
                                    </div>
                                </div>
                            );
                        })}
                        {/* Full Universe histogram: all companies with mcap > 500 */}
                        {(() => {
                            const fullSegment = { lower: 500, upper: Infinity };
                            const { binData, binCompanies: fullBinCompanies } = getSegmentData(fullSegment);
                            const totalInSegment = binData.reduce((sum, b) => sum + b.count, 0);
                            const chartData = {
                                labels,
                                datasets: [{
                                    label: "Full Universe (MCAP > 500)",
                                    data: binData.map((bin) => bin.count),
                                    backgroundColor: chartColors,
                                    borderColor: "#333",
                                    borderWidth: 1,
                                }],
                            };
                            const options = getOptions(fullBinCompanies, `#6 Full Universe MCAP > 500 (${totalInSegment} stocks)`);
                            return (
                                <div style={{
                                    padding: "10px",
                                    backgroundColor: "#fff",
                                    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
                                    borderRadius: "8px",
                                }}>
                                    <div style={{ height: "300px" }}>
                                        <Bar data={chartData} options={options} />
                                    </div>
                                    <div style={{ display: "flex", marginTop: "5px" }}>
                                        <SectorDoughnut title="Bottom Sectors" binCompanies={fullBinCompanies} binKeys={["<-10%", "-10% to -5%", "-5% to -2%"]} />
                                        <SectorDoughnut title="Top Sectors" binCompanies={fullBinCompanies} binKeys={["2% to 5%", "5% to 10%", "> 10%"]} />
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                ) : (
                    <p>Loading data...</p>
                )}

                <div style={{display: "flex", alignItems: "center", justifyContent: "center", marginTop: "20px"}}>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        style={{marginRight: "10px"}}
                    />
                    <button
                        onClick={loadHistoricData}
                        style={{
                            marginRight: "10px",
                            padding: "10px 20px",
                            backgroundColor: "#007BFF",
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                        }}
                    >
                        Load Historic Data
                    </button>
                    {loadingHistoric && (
                        <div className="spinner"></div>
                    )}
                </div>

                {showHistoricData && (
                    <div style={{ marginTop: "20px" }}>
                        {/* Default: Full Universe MCAP > 500 */}
                        <h3 style={{ marginBottom: "10px", textAlign: "center" }}>All Companies (MCAP &gt; 500)</h3>
                        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-around" }}>
                            {historicData.map((historicItem, index) => {
                                const { binData, binCompanies: hBinCompanies } = getHistoricSegmentBins(historicItem, { lower: 500, upper: Infinity });
                                const totalStocks = binData.reduce((sum, b) => sum + b.count, 0);
                                const chartData = {
                                    labels,
                                    datasets: [{
                                        label: `Snapshot: ${historicItem.snapshot_name}`,
                                        data: binData.map((bin) => bin.count),
                                        backgroundColor: chartColors,
                                        borderColor: "#333",
                                        borderWidth: 1,
                                    }],
                                };
                                const options = getOptions(hBinCompanies, `${historicItem.snapshot_name} (${totalStocks} stocks)`);
                                return (
                                    <div key={index} style={{ width: "23%", margin: "20px 0", height: "300px", boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)", padding: "10px", backgroundColor: "#fff" }}>
                                        <Bar data={chartData} options={options} />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Expandable MCAP segments */}
                        {mcapSegments.map((segment) => (
                            <div key={segment.label} style={{ marginTop: "15px", borderTop: "1px solid #ddd", paddingTop: "10px" }}>
                                <h3
                                    onClick={() => toggleSegment(segment.label)}
                                    style={{ cursor: "pointer", userSelect: "none", marginBottom: "10px" }}
                                >
                                    {expandedSegments[segment.label] ? "\u25BC" : "\u25B6"} {segment.label}
                                </h3>
                                {expandedSegments[segment.label] && (
                                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-around" }}>
                                        {historicData.map((historicItem, index) => {
                                            const { binData, binCompanies: hBinCompanies } = getHistoricSegmentBins(historicItem, segment);
                                            const totalStocks = binData.reduce((sum, b) => sum + b.count, 0);
                                            const chartData = {
                                                labels,
                                                datasets: [{
                                                    label: `Snapshot: ${historicItem.snapshot_name}`,
                                                    data: binData.map((bin) => bin.count),
                                                    backgroundColor: chartColors,
                                                    borderColor: "#333",
                                                    borderWidth: 1,
                                                }],
                                            };
                                            const options = getOptions(hBinCompanies, `${historicItem.snapshot_name} (${totalStocks} stocks)`);
                                            return (
                                                <div key={index} style={{ width: "23%", margin: "20px 0", height: "300px", boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)", padding: "10px", backgroundColor: "#fff" }}>
                                                    <Bar data={chartData} options={options} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {/* Modal for showing companies in bin */}
            <Modal
                isOpen={modalIsOpen}
                onRequestClose={() => setModalIsOpen(false)}
                style={modalStyles}
                contentLabel="Companies in Bin"
            >
                <h3>Companies in Selected Bin</h3>
                <p>Total Companies: <strong>{selectedBinData.length}</strong></p>
                <ul>
                    {selectedBinData.map((company, index) => (
                        <li key={index}>
                            <span style={{ fontWeight: "bold" }}>{company.name}</span> (
                            MCAP: {company.mcap !== undefined ? company.mcap.toFixed(2) : "N/A"},
                            Industry: {company.basicIndustry || "N/A"},
                            Deviation: <span
                                style={{ fontWeight: "bold", color: company.priceDeviation > 0 ? "green" : "red" }}>
                                {(company.priceDeviation * 100).toFixed(2)}%
                            </span>)
                        </li>
                    ))}
                </ul>
                <div style={{textAlign: "center", marginTop: "20px"}}>
                    <button
                        onClick={downloadCSV}
                        style={{
                            padding: "10px 15px",
                            borderRadius: "5px",
                            backgroundColor: "#28a745",
                            color: "white",
                            border: "none",
                            cursor: "pointer",
                            fontWeight: "bold",
                            marginRight: "10px",
                        }}
                    >
                        Download CSV
                    </button>
                    <button
                        onClick={() => setModalIsOpen(false)}
                        style={{
                            padding: "10px 15px",
                            borderRadius: "5px",
                            backgroundColor: "#dc3545",
                            color: "white",
                            border: "none",
                            cursor: "pointer",
                            fontWeight: "bold",
                        }}
                    >
                        Close
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default PriceDeviationHistogramInternal;
